import DOMPurify from 'dompurify';

/**
 * Security utility functions for sanitizing user input
 * to prevent XSS attacks and other injection vulnerabilities
 */

/**
 * Sanitizes HTML content to prevent XSS attacks
 * @param {string} html - The HTML content to sanitize
 * @returns {string} - Sanitized HTML
 */
export const sanitizeHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'frame', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    ALLOW_DATA_ATTR: false,
    ADD_ATTR: ['target'],
    FORCE_BODY: true,
    SANITIZE_DOM: true,
    USE_PROFILES: { html: true }
  });
};

/**
 * Sanitizes plain text input to prevent injection attacks
 * @param {string} text - The text to sanitize
 * @returns {string} - Sanitized text
 */
export const sanitizeText = (text) => {
  if (!text) return '';
  // First convert to string if not already
  const stringValue = String(text);
  // Remove any HTML/script tags
  return DOMPurify.sanitize(stringValue, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: []
  });
};

/**
 * Sanitizes content guidelines while preserving formatting like numbered lists
 * @param {string} text - The content guidelines text to sanitize
 * @returns {string} - Sanitized text with preserved formatting
 */
export const sanitizeContentGuidelines = (text) => {
  if (!text) return '';
  
  // First convert to string if not already
  const stringValue = String(text);
  
  // Use a more permissive DOMPurify configuration that preserves text formatting
  // but still removes dangerous content
  return DOMPurify.sanitize(stringValue, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
    // Preserve whitespace and line breaks
    FORBID_TAGS: ['script', 'style', 'iframe', 'frame', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'javascript'],
    // Allow common punctuation and formatting characters
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i
  });
};

/**
 * Sanitizes URL to prevent javascript: protocol and other malicious URLs
 * @param {string} url - The URL to sanitize
 * @returns {string} - Sanitized URL or empty string if invalid
 */
export const sanitizeUrl = (url) => {
  if (!url) return '';
  
  try {
    // Check if it's a valid URL
    const urlObj = new URL(url);
    
    // Only allow http and https protocols
    if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
      return '';
    }
    
    return urlObj.toString();
  } catch (e) {
    // If URL is invalid, return empty string
    return '';
  }
};

/**
 * Validates and sanitizes an email address
 * @param {string} email - The email to validate
 * @returns {string} - Sanitized email or empty string if invalid
 */
export const validateEmail = (email) => {
  if (!email) return '';
  
  // Basic email validation regex
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(email)) {
    return '';
  }
  
  return sanitizeText(email);
};

/**
 * Sanitizes an object by sanitizing all string values
 * @param {object} obj - The object to sanitize
 * @returns {object} - New object with sanitized values
 */
export const sanitizeObject = (obj) => {
  if (!obj || typeof obj !== 'object') return {};
  
  const sanitized = {};
  
  Object.keys(obj).forEach(key => {
    const value = obj[key];
    
    if (typeof value === 'string') {
      sanitized[key] = sanitizeText(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value);
    } else {
      sanitized[key] = value;
    }
  });
  
  return sanitized;
};

/**
 * Creates a safe HTML attribute value
 * @param {string} value - The attribute value to sanitize
 * @returns {string} - Sanitized attribute value
 */
export const sanitizeAttribute = (value) => {
  if (!value) return '';
  return value.replace(/[^\w\s-]/gi, '');
};
